import {
  addPendingDeletion,
  deleteMessage,
  getAllPendingDeletions as getAllPendingDeletionsDb,
  getMessage,
  getPendingDeletionsByThread as getPendingDeletionsDb,
  removePendingDeletion,
} from '@/lib/assistant-cloud-local/stores/indexeddb/db';
import { getRemoteIdForMessage } from '@/lib/utils/message';

const pendingDeletionsByThread = new Map<string, { messageId: string; remoteId: string }>();

// Server-persistence tenants never open IndexedDB: deletions ride the next chat POST body
// (`deletedMessageIds`) straight from the in-memory map, with no durable local queue.
let deletionQueueMode: 'idb' | 'memory' = 'idb';

export function setDeletionQueueMode(mode: 'idb' | 'memory'): void {
  deletionQueueMode = mode;
}

export async function getPendingDeletions(threadId: string): Promise<string[]> {
  if (deletionQueueMode === 'idb') {
    const dbDeletions = await getPendingDeletionsDb(threadId);
    if (dbDeletions.length) return dbDeletions;
  }

  const inMemory = pendingDeletionsByThread.get(threadId);
  return inMemory ? [inMemory.remoteId] : [];
}

export async function queueDeletion(threadId: string, messageId: string): Promise<void> {
  const message = deletionQueueMode === 'idb' ? await getMessage(messageId) : undefined;
  const remoteId = message?.remote_id || getRemoteIdForMessage(messageId) || messageId;

  pendingDeletionsByThread.set(threadId, { messageId, remoteId });
  if (deletionQueueMode === 'idb') {
    await addPendingDeletion(threadId, messageId, remoteId);
  }
}

export async function getAllPendingDeletions(): Promise<string[]> {
  if (deletionQueueMode === 'idb') {
    const dbDeletions = await getAllPendingDeletionsDb();
    if (dbDeletions.length) return dbDeletions.map(d => d.message_id);
  }

  return Array.from(pendingDeletionsByThread.values()).map(d => d.messageId);
}

export async function confirmDeletions(localMessageIds: string[]): Promise<void> {
  for (const [threadId, deletion] of pendingDeletionsByThread) {
    if (localMessageIds.includes(deletion.messageId)) {
      pendingDeletionsByThread.delete(threadId);
    }
  }

  if (deletionQueueMode !== 'idb') return;
  await Promise.all(
    localMessageIds.map(async messageId => {
      await deleteMessage(messageId);
      await removePendingDeletion(messageId);
    }),
  );
}
